/**
 * Managed-mode OCS scoping for MCP tool handlers.
 *
 * Ported from apps/mcp-server/src/ocs-scoping.ts. Managed orgs share ONE master
 * reseller token, so scoping is the only thing keeping them apart: a KV key that
 * carries no tenant, or an OCS call that takes a caller-supplied account id, is
 * open across the whole fleet unless a preflight proves ownership first.
 *
 * In today's stdio entrypoint `managedAuth` is `{}` — the operator supplies
 * their own OCS token, so that token is already the boundary and every helper
 * here is a no-op. The helpers exist so the fork stops diverging from the
 * Worker: the moment stdio is handed a managed context or a real KV binding,
 * the guards are already in place instead of being remembered later.
 *
 * Only the ICCID-ownership half of the Worker module is ported. `scopeAccountParams`
 * and `scopedOcsCall` belong to the OCS write path, which this fork does not
 * route through here; porting them would be dead code.
 */

import { OcsClient } from "./client.js";

export interface ManagedAuth {
  managed?: boolean;
  accountId?: string;
  /** Default OCS account for BYO/reseller tokens (mirrors API preferredAccountId). */
  preferredAccountId?: string;
}

/** Numeric OCS sub-account id for managed scoping, or undefined when not applicable. */
export function managedOwnAccountId(
  auth: Pick<ManagedAuth, "managed" | "accountId">,
): number | undefined {
  if (!auth.managed || auth.accountId === undefined) return undefined;
  const n = Number(auth.accountId);
  return Number.isFinite(n) ? n : undefined;
}

/** Extract the OCS account id from a getSubscriber (or nested subscriber) payload. */
export function subscriberAccountId(data: unknown): number | undefined {
  if (!data || typeof data !== "object") return undefined;
  const root = data as Record<string, unknown>;
  const sub =
    root.subscriber && typeof root.subscriber === "object"
      ? (root.subscriber as Record<string, unknown>)
      : root;
  const raw = sub.accountId ?? sub.account_id ?? sub.account;
  if (raw === undefined || raw === null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** True when a managed org is trying to access a subscriber outside its sub-account. */
export function isManagedSubscriberDenied(
  auth: Pick<ManagedAuth, "managed" | "accountId">,
  subscriberData: unknown,
): boolean {
  const ownId = managedOwnAccountId(auth);
  if (ownId === undefined) return false;
  const subAccountId = subscriberAccountId(subscriberData);
  if (subAccountId === undefined) return true;
  return subAccountId !== ownId;
}

/** Pre-flight ICCID guard for managed sessions (shared master token). */
export async function assertManagedIccidAccess(
  baseUrl: string,
  auth: Pick<ManagedAuth, "managed" | "accountId"> & { token: string },
  iccid: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (managedOwnAccountId(auth) === undefined) return { ok: true };
  try {
    const client = new OcsClient(baseUrl, auth.token);
    const data = await client.call("getSingleSubscriber", { iccid });
    if (isManagedSubscriberDenied(auth, data)) {
      return { ok: false, error: "Subscriber not found" };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
